import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import { Activity, Dumbbell, MessageCircle, Salad, Check } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const ACCENT = "#3730A3";

const FEATURES = [
  { icon: Dumbbell, title: "Programmes en 10 minutes", desc: "Bibliothèque d'exercices, blocs réutilisables, duplication de semaines : vos clients reçoivent leur programme sur leur téléphone, avec vidéos et charges prescrites." },
  { icon: Activity, title: "Suivi qui détecte le décrochage", desc: "Séances logguées, RPE, records, check-ins hebdomadaires — et des alertes automatiques quand un client devient inactif. Vous relancez avant qu'il ne parte, pas après." },
  { icon: Salad, title: "Nutrition intégrée", desc: "Plans de repas par objectif calorique, recettes modifiables, macros calculées. Fini le PDF de « meal plan » que personne n'ouvre." },
  { icon: MessageCircle, title: "Messagerie professionnelle", desc: "Toutes les conversations au même endroit, séparées de votre WhatsApp perso. Photos, vocaux, médias de progression." },
];

const FAQ = [
  { q: "L'application est-elle incluse pour mes clients ?", a: "Oui. Chaque client invité télécharge l'application mobile (iOS et Android) gratuitement : programmes, séances à logguer, nutrition, messagerie et check-ins." },
  { q: "Quelle différence avec Hexfit, Gymkee ou Nolio ?", a: "Le cœur de CoachOS est la rétention : détection automatique des clients qui décrochent (inactivité, check-ins sans réponse, baisse d'énergie) avec relances au bon moment. C'est le seul angle qui protège directement votre revenu mensuel." },
  { q: "Combien ça coûte ?", a: "À partir de 39 €/mois sans engagement — soit moins qu'une séance. L'application client est incluse, sans surcoût par client." },
  { q: "Combien de temps pour démarrer ?", a: "Une dizaine de minutes : créez votre compte, invitez vos clients par email, assignez un premier programme." },
];

const ApplicationCoachSportif = () => (
  <div className="min-h-screen" style={{ background: "#ffffff" }}>
    <Head>
      <title>Application coach sportif : suivi client, programmes et relances — CoachOS</title>
      <meta name="description" content="L'application pour coach sportif qui détecte les clients qui décrochent : programmes en 10 min, suivi mobile, nutrition et relances automatiques. Dès 39 €/mois." />
      <link rel="canonical" href="https://www.coach-os.eu/application-coach-sportif" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "CoachOS",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web, iOS, Android",
          description: "Application de gestion pour coachs sportifs : programmes, suivi client, nutrition, messagerie et alertes de décrochage.",
          offers: { "@type": "Offer", price: "39", priceCurrency: "EUR" },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}
      </script>
    </Head>
    <Navbar />
    <main className="container max-w-5xl pt-32 pb-24">
      {/* Hero aligné à gauche */}
      <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#3730A3]">Application coach sportif</p>
      <h1 className="max-w-3xl text-4xl md:text-6xl font-extrabold tracking-tighter leading-none text-[#0e1220] mb-6">
        L'application que vos clients ouvrent — et qui vous dit <span style={{ color: ACCENT }}>qui décroche</span>.
      </h1>
      <p className="max-w-[56ch] text-lg text-[#0e1220]/60 leading-relaxed mb-8">
        Programmes, suivi des séances, nutrition et messagerie dans une seule application
        pour coach sportif — avec des alertes automatiques quand un client s'éloigne.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mb-16">
        <Link to="/#pricing" className="inline-flex h-12 items-center justify-center rounded-full bg-[#14142B] px-7 font-semibold text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition hover:bg-[#23233d] active:scale-[0.98]">
          Commencer — dès 39 €/mois
        </Link>
        <a href="https://calendly.com/gregory-scafarto/30min" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full px-6 h-12 font-semibold text-[#0e1220]/90 border border-[#dfe2ec] bg-white hover:bg-[#eef0f6]">
          Demander une démo
        </a>
      </div>

      {/* Fonctionnalités — zig-zag 2 colonnes */}
      <div className="grid gap-x-14 gap-y-12 md:grid-cols-2 mb-20">
        {FEATURES.map(({ icon: Icon, title, desc }) => (
          <div key={title}>
            <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${ACCENT}22`, color: ACCENT }}>
              <Icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-bold text-[#0e1220] mb-2">{title}</h2>
            <p className="text-[#0e1220]/55 leading-relaxed max-w-[52ch]">{desc}</p>
          </div>
        ))}
      </div>

      {/* Réassurance */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#0e1220]/50 mb-20">
        {["Application client iOS & Android incluse", "Sans engagement", "Mise en route en 10 minutes"].map(t => (
          <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: ACCENT }} />{t}</span>
        ))}
      </div>

      {/* FAQ */}
      <h2 className="text-2xl font-extrabold tracking-tight text-[#0e1220] mb-8">Questions fréquentes</h2>
      <div className="divide-y divide-[#e7e9f1] mb-16">
        {FAQ.map(({ q, a }) => (
          <div key={q} className="py-6">
            <h3 className="font-semibold text-[#0e1220] mb-2">{q}</h3>
            <p className="text-[#0e1220]/55 leading-relaxed max-w-[70ch]">{a}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#e7e9f1] bg-white shadow-[0_1px_3px_rgba(16,20,40,0.05)] p-8">
        <h2 className="text-xl font-bold text-[#0e1220] mb-2">Testez avec vos vrais clients</h2>
        <p className="text-[#0e1220]/55 mb-5">Créez votre compte, invitez deux clients, assignez un programme — et jugez sur pièce.</p>
        <Link to="/#pricing" className="inline-flex h-11 items-center rounded-full bg-[#14142B] px-6 font-semibold text-white shadow-[0_8px_20px_rgba(20,20,43,0.16)] transition hover:bg-[#23233d]">
          Voir les offres
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

export default ApplicationCoachSportif;
